import { useMemo, useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import { Eye, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react'
import AppLayout from '@/components/AppLayout'
import PageTitle from '@/components/PageTitle'
import {
  applyTableState,
  type Column,
  DataTable,
  DataTableToolbar,
  useTableState,
} from '@/components/blocks/data-table'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { DropdownMenu, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/DropdownMenu'
import { EmptyState } from '@/components/ui/EmptyState'
import { Tabs } from '@/components/ui/Tabs'
import { ConfirmDialog, type ConfirmRequest } from '@/components/shared'
import { cn } from '@/lib/utils'
import type { CardOptions } from '@/components/cards/CardForm'
import CardImageModal, { type CardImage } from '@/components/cards/CardImageModal'

interface Card {
  id: string
  cr_name: string
  name: string | null
  description: string | null
  count: number
  arena: number
  rarity: string
  card_type: string
  transport: string | null
  target_air: boolean
  target_ground: boolean
  focus: string
  range_tiles: string | null
  speed: string | null
  hit_speed: string | null
  first_hit_speed: string | null
  cost: number
  form_size: string | null
  cr_image_url: string | null
  concept_image_url: string | null
  design_image_url: string | null
}

interface CardsIndexProps {
  cards: Card[]
  options: CardOptions
  permissions: {
    create: boolean
    update: boolean
    destroy: boolean
    art: boolean
  }
}

type TypeTab = 'all' | 'troop' | 'spell' | 'building'

const TYPE_TABS: { label: string; value: TypeTab }[] = [
  { label: 'All', value: 'all' },
  { label: 'Troop', value: 'troop' },
  { label: 'Spell', value: 'spell' },
  { label: 'Building', value: 'building' },
]

const RARITY_CLASS: Record<string, string> = {
  rare: 'text-rarity-rare',
  epic: 'text-rarity-epic',
  legendary: 'text-rarity-legendary',
}

function rarityClass(rarity: string): string {
  return RARITY_CLASS[rarity] ?? 'text-rarity-common'
}

function targetsLabel(card: Card): string {
  if (card.target_air && card.target_ground) return 'Air & Ground'
  if (card.target_air) return 'Air'
  if (card.target_ground) return 'Ground'
  return '—'
}

function cardImages(card: Card): CardImage[] {
  const images: CardImage[] = []
  if (card.cr_image_url) images.push({ url: card.cr_image_url, label: 'Clash Royale' })
  if (card.concept_image_url) images.push({ url: card.concept_image_url, label: 'Concept' })
  if (card.design_image_url) images.push({ url: card.design_image_url, label: 'Design' })
  return images
}

export default function CardsIndex({ cards, options, permissions }: CardsIndexProps) {
  const { state, setState } = useTableState({ pageSize: 25, sortKey: 'arena', sortDir: 'asc' })
  const [type, setType] = useState<TypeTab>('all')
  const [preview, setPreview] = useState<{ card: Card; startIndex: number } | null>(null)
  const [confirmRequest, setConfirmRequest] = useState<ConfirmRequest | null>(null)

  /* Columns carry their own sort keys and alignment, so the hand-rolled
     <SortHead> and the bespoke comparator both go away — applyTableState does
     search, filter, sort and pagination from the same declaration. */
  const columns: Column<Card>[] = useMemo(
    () => [
      {
        key: 'cr_name',
        header: 'Name',
        width: 'minmax(260px,1.6fr)',
        sortable: true,
        cell: (card) => {
          const images = cardImages(card)
          return (
            <span className="flex items-center gap-3">
              {images.length > 0 && (
                <span className="flex shrink-0 -space-x-2">
                  {images.map((img, i) => (
                    <button
                      key={img.label}
                      type="button"
                      onClick={() => setPreview({ card, startIndex: i })}
                      aria-label={`View ${img.label} image`}
                      className="focus-visible:outline-accent rounded-full transition-transform duration-100 ease-out outline-none hover:z-10 hover:scale-110 focus-visible:outline-2"
                    >
                      <img
                        src={img.url}
                        alt=""
                        width={64}
                        height={64}
                        loading="lazy"
                        className="border-bg bg-bg-subtle size-8 rounded-full border-2 object-cover"
                      />
                    </button>
                  ))}
                </span>
              )}
              <span className="min-w-0">
                <Link
                  href={`/cards/${card.id}`}
                  className="text-fg block truncate font-medium hover:underline"
                >
                  {card.name || card.cr_name}
                </Link>
                {card.name && (
                  <span className="text-fg-subtle block truncate text-xs">{card.cr_name}</span>
                )}
              </span>
            </span>
          )
        },
      },
      {
        key: 'card_type',
        header: 'Type',
        width: '110px',
        sortable: true,
        cell: (card) => <span className="capitalize">{card.card_type}</span>,
      },
      {
        key: 'rarity',
        header: 'Rarity',
        width: '120px',
        sortable: true,
        cell: (card) => (
          <Badge variant="outline" className={cn('capitalize', rarityClass(card.rarity))}>
            {card.rarity}
          </Badge>
        ),
      },
      { key: 'cost', header: 'Cost', width: '80px', align: 'end', sortable: true },
      { key: 'arena', header: 'Arena', width: '80px', align: 'end', sortable: true },
      { key: 'count', header: 'Count', width: '80px', align: 'end', sortable: true },
      {
        key: 'transport',
        header: 'Transport',
        width: '110px',
        cell: (card) => <span className="text-fg-muted">{card.transport ?? '—'}</span>,
      },
      {
        key: 'targets',
        header: 'Targets',
        width: '130px',
        cell: (card) => <span className="text-fg-muted">{targetsLabel(card)}</span>,
      },
      {
        key: 'focus',
        header: 'Focus',
        width: '110px',
        cell: (card) => <span className="text-fg-muted capitalize">{card.focus}</span>,
      },
      {
        key: 'form_size',
        header: 'Form Size',
        width: '110px',
        cell: (card) => <span className="text-fg-muted">{card.form_size ?? '—'}</span>,
      },
    ],
    [],
  )

  const scoped = useMemo(
    () => (type === 'all' ? cards : cards.filter((c) => c.card_type === type)),
    [cards, type],
  )
  const table = useMemo(() => applyTableState(scoped, columns, state), [scoped, columns, state])

  const requestDelete = (card: Card) =>
    setConfirmRequest({
      title: `Delete ${card.name || card.cr_name}?`,
      description: 'The card and its artwork are removed. This cannot be undone.',
      confirmLabel: 'Delete Card',
      destructive: true,
      onConfirm: () => router.delete(`/cards/${card.id}`, { preserveScroll: true }),
    })

  return (
    <AppLayout
      header={<PageTitle title="Cards" subtitle={`${cards.length} cards`} />}
      headerActions={
        permissions.create && (
          <Button asChild>
            <Link href="/cards/new">
              <Plus />
              New Card
            </Link>
          </Button>
        )
      }
    >
      <Head title="Cards" />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Tabs
          aria-label="Card type"
          options={TYPE_TABS.map((tab) => ({
            value: tab.value,
            label:
              tab.value === 'all'
                ? `All ${cards.length}`
                : `${tab.label} ${cards.filter((c) => c.card_type === tab.value).length}`,
          }))}
          value={type}
          onValueChange={(next) => {
            setType(next)
            setState({ search: '', filters: {} })
          }}
        />
      </div>

      <div className="space-y-3">
        <DataTableToolbar
          state={state}
          onStateChange={setState}
          searchPlaceholder="Search cards by name…"
          filters={[
            {
              key: 'rarity',
              label: 'All rarities',
              options: [
                { value: '', label: 'All rarities' },
                ...options.rarities.map((r) => ({ value: r, label: r })),
              ],
            },
          ]}
        />

        <DataTable
          columns={columns}
          rows={table.rows}
          total={table.total}
          state={state}
          onStateChange={setState}
          rowKey={(card) => card.id}
          minWidth={1240}
          empty={
            <EmptyState
              title="No cards match your filters."
              action={
                <Button variant="outline" onClick={() => setState({ search: '', filters: {} })}>
                  Clear Filters
                </Button>
              }
            />
          }
          rowActions={(card) => (
            <DropdownMenu
              trigger={
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Actions for ${card.name || card.cr_name}`}
                >
                  <MoreHorizontal />
                </Button>
              }
            >
              <DropdownMenuItem asChild>
                <Link href={`/cards/${card.id}`}>
                  <Eye />
                  View Card
                </Link>
              </DropdownMenuItem>
              {permissions.update && (
                <DropdownMenuItem asChild>
                  <Link href={`/cards/${card.id}/edit`}>
                    <Pencil />
                    Edit Card
                  </Link>
                </DropdownMenuItem>
              )}
              {permissions.destroy && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem destructive onClick={() => requestDelete(card)}>
                    <Trash2 />
                    Delete Card
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenu>
          )}
        />
      </div>

      <CardImageModal
        open={preview !== null}
        onOpenChange={(open) => !open && setPreview(null)}
        cardName={preview ? preview.card.name || preview.card.cr_name : ''}
        images={preview ? cardImages(preview.card) : []}
        startIndex={preview?.startIndex ?? 0}
      />

      <ConfirmDialog request={confirmRequest} onClose={() => setConfirmRequest(null)} />
    </AppLayout>
  )
}
